/*!
 * TakeLevel — ONE owner of "how loud is this take, and will the listener hear it".
 *
 * A microphone take arrives at whatever level the device gives us. Desktop
 * Chrome takes peak at −10…0 dBFS; iOS Safari / Chrome-on-iOS with raw
 * constraints (echoCancellation / autoGainControl off — the studio's default,
 * the right choice for a studio mic) peak 20–30 dB lower: /vocal
 * `record_take.peakDb`, both shards, 90 days to 2026-09-23 — 10 of 20 iOS takes
 * between −46 and −34 dBFS, desktop takes mostly −20…0. A studio that mixes such
 * a take under a mastered backing track hands the singer a file where the
 * voice sits 30–40 dB under the music, and its noise gate (absolute threshold
 * −45 dBFS, on by default) turns "quiet" into "silent": measured 2026-09-23
 * with the studio's own gate in an OfflineAudioContext — speech-like input
 * peaking at −45 dBFS loses 7.8 dB, at −50 dBFS it is gone entirely.
 * Support 2026-09-20 (iPad, Chrome on iOS, /fr/recording-studio): «On entend
 * pas la voix» — two takes with real sample counts, an MP3 export, no voice.
 *
 * Contract:
 *   analyze(buffer)              → {peak, peakDb, levelDb, noiseDb, snrDb, frames, durationS}
 *       levelDb — 98th percentile of 50-ms frame peaks: the voice, robust to
 *                 a single pop and to long silences before the singer starts;
 *       noiseDb — 10th percentile of 50-ms frame RMS: the floor between phrases;
 *       snrDb   — levelDb − noiseDb. A flat hiss has frame peaks ≈ RMS + 11 dB;
 *                 a voice over a floor has 30–60.
 *   normalizeGainDb(stats, opts) → dB to ADD so a quiet take lands with its
 *       levelDb at opts.targetDb (−9): 0 when the take is already there or
 *       louder (never attenuates), 0 when there is nothing to raise
 *       (levelDb < −66: dead mic, digital silence), 0 when there is nothing but
 *       noise (snrDb < 18), capped at maxBoostDb (36) and so that the true
 *       peak stays at or under peakCeilingDb (−1 dBFS).
 *   applyGain(buffer, gainDb)    → multiplies every channel in place; returns it.
 *   gateCapDb(levelDb, margin)   → the highest gate threshold that still lets
 *       THIS voice through (levelDb − 8 by default); clampGate(thr, cap).
 *
 * `buffer` is an AudioBuffer or {channels: [Float32Array…], sampleRate}; integer
 * channels (Int16Array) say how to read them with `scale` (1/32768) — the studio's
 * rehearsal is kept as Int16 and is measured without a Float32 copy.
 * Consumers: /recording-studio, /vocal (TakeLevelHasOneOwnerTest); /dictaphone
 * measures every finished take for the empty-take verdict and the `take_level`
 * telemetry, and does not raise (DictaphoneDeadMicIsNamedTest). Guards:
 * tests/js/take-level.test.mjs, tests/playwright/studio-quiet-take-is-heard.mjs.
 */
(function (root) {
    'use strict';

    var FRAME_MS = 50;
    var DEFAULTS = {
        targetDb: -9,        // levelDb after the boost — the quiet end of a normal desktop take
        maxBoostDb: 36,
        minLevelDb: -66,     // below this there is no voice to raise
        minSnrDb: 18,        // below this the take is noise, and a louder hiss helps nobody
        peakCeilingDb: -1,
        gateMarginDb: 8,
        toastFromDb: 6       // consumers tell the singer about boosts from here up
    };

    function dbOf(x) { return x > 1e-9 ? 20 * Math.log10(x) : -180; }

    function pct(sorted, p) {
        if (!sorted.length) return -180;
        var i = Math.round((sorted.length - 1) * p);
        if (i < 0) i = 0;
        if (i > sorted.length - 1) i = sorted.length - 1;
        return sorted[i];
    }

    function channelsOf(buf) {
        if (!buf) return [];
        if (typeof buf.getChannelData === 'function') {
            var out = [];
            for (var c = 0; c < buf.numberOfChannels; c++) out.push(buf.getChannelData(c));
            return out;
        }
        if (buf.channels && buf.channels.length) return buf.channels;
        return [];
    }

    function analyze(buf) {
        var chans = channelsOf(buf);
        var sr = (buf && buf.sampleRate) || 48000;
        var n = chans.length ? chans[0].length : 0;
        var frame = Math.max(1, Math.round(sr * FRAME_MS / 1000));
        var k = (buf && buf.scale) || 1;
        var peaksDb = [], rmsDb = [], peak = 0;
        for (var s = 0; s < n; s += frame) {
            var e = Math.min(n, s + frame), fp = 0, sum = 0, cnt = 0;
            for (var c = 0; c < chans.length; c++) {
                var d = chans[c];
                for (var i = s; i < e; i++) {
                    var v = d[i] * k;
                    var a = v < 0 ? -v : v;
                    if (a > fp) fp = a;
                    sum += v * v;
                    cnt++;
                }
            }
            if (fp > peak) peak = fp;
            peaksDb.push(dbOf(fp));
            rmsDb.push(dbOf(Math.sqrt(sum / (cnt || 1))));
        }
        peaksDb.sort(function (a, b) { return a - b; });
        rmsDb.sort(function (a, b) { return a - b; });
        var levelDb = pct(peaksDb, 0.98);
        var noiseDb = pct(rmsDb, 0.10);
        return {
            peak: peak,
            peakDb: dbOf(peak),
            levelDb: levelDb,
            noiseDb: noiseDb,
            snrDb: levelDb - noiseDb,
            frames: peaksDb.length,
            durationS: n / sr
        };
    }

    function normalizeGainDb(stats, opts) {
        var o = Object.assign({}, DEFAULTS, opts || {});
        if (!stats || !isFinite(stats.levelDb) || !isFinite(stats.peakDb)) return 0;
        if (stats.levelDb >= o.targetDb) return 0;
        if (stats.levelDb < o.minLevelDb) return 0;
        if (!(stats.snrDb >= o.minSnrDb)) return 0;
        var g = Math.min(o.maxBoostDb, o.targetDb - stats.levelDb, o.peakCeilingDb - stats.peakDb);
        if (!(g > 0)) return 0;
        return Math.round(g * 10) / 10;
    }

    function applyGain(buf, gainDb) {
        if (!gainDb) return buf;
        var k = Math.pow(10, gainDb / 20);
        var chans = channelsOf(buf);
        for (var c = 0; c < chans.length; c++) {
            var d = chans[c];
            for (var i = 0; i < d.length; i++) d[i] *= k;
        }
        return buf;
    }

    function gateCapDb(levelDb, marginDb) {
        if (levelDb == null || !isFinite(levelDb)) return null;
        return levelDb - (marginDb == null ? DEFAULTS.gateMarginDb : marginDb);
    }

    function clampGate(thresholdDb, capDb) {
        if (capDb == null || !isFinite(capDb)) return thresholdDb;
        return Math.min(thresholdDb, capDb);
    }

    function round1(x) { return Math.round(x * 10) / 10; }

    var api = {
        analyze: analyze,
        normalizeGainDb: normalizeGainDb,
        applyGain: applyGain,
        gateCapDb: gateCapDb,
        clampGate: clampGate,
        round1: round1,
        DEFAULTS: DEFAULTS,
        FRAME_MS: FRAME_MS
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.TakeLevel = api;
})(typeof window !== 'undefined' ? window : (typeof self !== 'undefined' ? self : null));
